(function () {
  const productCache = new Map();
  const oneLinerCache = new Map();
  const STORAGE_KEY = 'foilFinderState_v1';

  async function loadProduct(handle) {
    if (productCache.has(handle)) return productCache.get(handle);
    const promise = fetch('/products/' + encodeURIComponent(handle) + '.js', {
      headers: { Accept: 'application/json' },
    }).then((r) => (r.ok ? r.json() : null)).catch(() => null);
    productCache.set(handle, promise);
    return promise;
  }

  // Metafields aren't available on /products/{handle}.js, so fetch the
  // one-liner via the Section Rendering API endpoint (foil-finder-meta).
  async function loadOneLiner(handle) {
    if (oneLinerCache.has(handle)) return oneLinerCache.get(handle);
    const promise = fetch(
      '/products/' + encodeURIComponent(handle) + '?section_id=foil-finder-meta'
    )
      .then((r) => (r.ok ? r.text() : null))
      .then((txt) => {
        if (!txt) return null;
        try {
          const doc = new DOMParser().parseFromString(txt, 'text/html');
          const el = doc.getElementById('foil-finder-meta-data');
          if (!el) return null;
          const data = JSON.parse(el.textContent);
          return data && data.one_liner ? String(data.one_liner) : null;
        } catch (e) {
          return null;
        }
      })
      .catch(() => null);
    oneLinerCache.set(handle, promise);
    return promise;
  }

  // Minimal RFC4180-ish CSV parser (handles quoted fields, escaped quotes,
  // commas/newlines inside quotes, CRLF, and a leading BOM). No dependencies.
  function parseCSV(text) {
    if (text.charCodeAt(0) === 0xfeff) text = text.slice(1);
    const rows = [];
    let field = '';
    let row = [];
    let inQuotes = false;
    let i = 0;
    while (i < text.length) {
      const c = text[i];
      if (inQuotes) {
        if (c === '"') {
          if (text[i + 1] === '"') { field += '"'; i += 2; continue; }
          inQuotes = false; i += 1; continue;
        }
        field += c; i += 1; continue;
      }
      if (c === '"') { inQuotes = true; i += 1; continue; }
      if (c === ',') { row.push(field); field = ''; i += 1; continue; }
      if (c === '\r') { i += 1; continue; }
      if (c === '\n') { row.push(field); rows.push(row); field = ''; row = []; i += 1; continue; }
      field += c; i += 1;
    }
    if (field.length > 0 || row.length > 0) { row.push(field); rows.push(row); }
    return rows.filter((r) => r.some((cell) => cell.trim() !== ''));
  }

  class FoilFinder extends HTMLElement {
    connectedCallback() {
      if (this._mounted) return;
      this._mounted = true;

      this.dataUrl = this.dataset.url;
      this.csvUrl = (this.dataset.csvUrl || '').trim();
      this.resultsLayout = (this.dataset.resultsLayout || 'classic').trim();
      this.disciplinesEl = this.querySelector('[data-finder-disciplines]');
      this.abilitiesEl = this.querySelector('[data-finder-abilities]');
      this.weightsEl = this.querySelector('[data-finder-weights]');
      this.resultsEl = this.querySelector('[data-finder-results]');
      this.statusEl = this.querySelector('[data-finder-status]');
      this.statusbarEl = this.querySelector('.foil-finder__statusbar');
      this.wizardEl = this.querySelector('[data-finder-wizard]');
      this.startmodeEl = this.querySelector('[data-finder-startmode]');
      this.resultsbarEl = this.querySelector('[data-finder-resultsbar]');
      this.viewToggleEl = this.querySelector('[data-finder-viewtoggle]');
      this.viewSegEl = this.querySelector('.foil-finder__seg');
      this.summaryEl = this.querySelector('[data-finder-summary]');
      this.introEl = this.querySelector('[data-finder-intro]');
      this.titleEl = this.querySelector('[data-finder-title]');
      // Defaults shown on the landing screen and the results header.
      this.defaultTitle = this.titleEl ? this.titleEl.textContent.trim() : '';
      this.defaultIntro = this.introEl ? this.introEl.textContent.trim() : '';
      this.progressEl = this.querySelector('[data-finder-progress]');
      this.headerEl = this.querySelector('[data-finder-header]');
      this.goBtn = this.querySelector('[data-finder-go]');
      this.backBtn = this.querySelector('[data-finder-back]');
      this.nextBtn = this.querySelector('[data-finder-next]');
      this.toggleEl = this.querySelector('.foil-finder__toggle');
      this.stepsEl = this.querySelector('.foil-finder__steps');
      this.definitionsEl = this.querySelector('[data-finder-definitions-panel]');
      this.defHintEl = this.querySelector('[data-finder-def-hint]');

      // Discipline ability definitions (embedded JSON in the section).
      this.definitions = {};
      const defScript = this.querySelector('[data-finder-definitions]');
      if (defScript) {
        try {
          this.definitions = JSON.parse(defScript.textContent);
        } catch (e) {
          console.error('[foil-finder] Could not parse ability definitions.', e);
        }
      }

      // Step machine. The rider can choose where to start via the toggle;
      // Weight is always the final step.
      this.stepConfigs = {
        ability: { key: 'ability', label: 'Level' },
        weight: { key: 'weight', label: 'Weight' },
        discipline: { key: 'discipline', label: 'Discipline' },
      };
      this.orderings = {
        ability: ['ability', 'discipline', 'weight'],
        discipline: ['discipline', 'ability', 'weight'],
      };
      this.startMode = 'discipline';
      this.stepOrder = this.orderings[this.startMode].map((k) => this.stepConfigs[k]);

      // Beginner and Advanced are mutually exclusive — picking one greys
      // out the other. Intermediate can combine with either.
      this.exclusiveAbilities = ['Beginner', 'Advanced'];

      // Display-only renaming. The underlying data value (used for matching
      // against the CSV / JSON) is unchanged — only the visible text differs.
      this.disciplineLabels = {
        Winging: 'Wing',
        Downwind: 'Downwind SUP',
        Parawinging: 'Parawing',
      };
      this.stepEls = {};
      this.querySelectorAll('[data-finder-step]').forEach((el) => {
        this.stepEls[el.getAttribute('data-finder-step')] = el;
      });
      this.stepIndex = 0;
      this.completed = false;
      this.started = false;

      this.state = { disciplines: new Set(), abilities: new Set(), weight: null };

      this.loadData();
      this.bindEvents();

      // Re-measure the toggle thumb on resize (button padding/font changes
      // at the 768px breakpoint). Guarded internally when not visible.
      let rt;
      window.addEventListener('resize', () => {
        clearTimeout(rt);
        rt = setTimeout(() => {
          this.positionToggleThumb();
          this.positionViewThumb();
        }, 120);
      });
    }

    async loadData() {
      this.setStatus('Loading…');
      try {
        const res = await fetch(this.dataUrl, { headers: { Accept: 'application/json' } });
        if (!res.ok) throw new Error('Failed to load finder data');
        this.data = await res.json();

        // Optional CSV override: keep filters/weight-bands from the JSON
        // (the stable part), replace only the recommendation matrix.
        if (this.csvUrl) {
          try {
            const csvRecs = await this.loadCsvRecommendations(this.csvUrl);
            if (csvRecs.length) {
              this.data.recommendations = csvRecs;
            } else {
              console.warn('[foil-finder] CSV produced no usable rows; using default data.');
            }
          } catch (csvErr) {
            console.error('[foil-finder] CSV load failed; using default data.', csvErr);
          }
        }

        this.renderFilters();
        const saved = this.readState();
        if (saved) {
          this.restore(saved);
        } else {
          this.showLanding();
        }
        this.setStatus('');
      } catch (e) {
        this.setStatus('Could not load finder data. Please refresh.');
        console.error('[foil-finder]', e);
      }
    }

    async loadCsvRecommendations(url) {
      const res = await fetch(url, { cache: 'no-cache' });
      if (!res.ok) throw new Error('CSV fetch failed: HTTP ' + res.status);
      const rows = parseCSV(await res.text());
      if (rows.length < 2) return [];

      const headers = rows[0].map((h) => h.trim().toLowerCase());
      const col = (name) => headers.indexOf(name);
      const required = ['discipline', 'ability', 'weight_label', 'category', 'name', 'handle', 'variant_id'];
      const missing = required.filter((h) => col(h) === -1);
      if (missing.length) throw new Error('CSV missing column(s): ' + missing.join(', '));

      // Blank or "*" both mean "any".
      const wild = (v) => {
        const t = (v || '').trim();
        return t === '' || t === '*' ? '*' : t;
      };

      const out = [];
      for (let i = 1; i < rows.length; i += 1) {
        const r = rows[i];
        const get = (name) => {
          const idx = col(name);
          return idx > -1 && r[idx] != null ? String(r[idx]).trim() : '';
        };
        const handle = get('handle');
        const name = get('name');
        const category = get('category');
        const variantId = get('variant_id');
        // Skip incomplete rows rather than render broken cards.
        if (!handle || !name || !category || !variantId) continue;
        out.push({
          discipline: wild(get('discipline')),
          ability: wild(get('ability')),
          weight_label: wild(get('weight_label')),
          category: category,
          family: get('family'),
          size: get('size'),
          name: name,
          handle: handle,
          variant_id: variantId,
        });
      }
      return out;
    }

    renderFilters() {
      const f = this.data.filters;
      this.disciplinesEl.innerHTML = f.disciplines
        .map((d) => this.chipHTML('discipline', d, this.discLabel(d)))
        .join('');
      this.abilitiesEl.innerHTML = f.abilities
        .map((a) => this.chipHTML('ability', a, a))
        .join('');
      this.weightsEl.innerHTML = f.weight_bands
        .map((w) => this.chipHTML('weight', w.label, w.label, true))
        .join('');
      this.applyAbilityConstraints();
    }

    // Beginner ↔ Advanced are mutually exclusive: selecting one disables
    // (greys out) the other. Intermediate is unaffected.
    applyAbilityConstraints() {
      if (!this.abilitiesEl) return;
      const [a, b] = this.exclusiveAbilities;
      const lock = {};
      lock[a] = this.state.abilities.has(b);
      lock[b] = this.state.abilities.has(a);
      this.abilitiesEl.querySelectorAll('.foil-finder__chip').forEach((chip) => {
        const v = chip.dataset.value;
        if (Object.prototype.hasOwnProperty.call(lock, v)) {
          const off = lock[v];
          chip.disabled = off;
          chip.classList.toggle('is-disabled', off);
          chip.setAttribute('aria-disabled', off ? 'true' : 'false');
        }
      });
    }

    chipHTML(group, value, label, single = false) {
      const role = single ? 'radio' : 'checkbox';
      return (
        '<button type="button" class="foil-finder__chip" role="' + role +
        '" aria-checked="false" data-group="' + group +
        '" data-value="' + this.escape(value) + '">' +
        this.escape(label) + '</button>'
      );
    }

    escape(s) {
      return String(s).replace(/[&<>"']/g, (c) => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
      }[c]));
    }

    // Friendly display name for a discipline; falls back to the raw value.
    discLabel(value) {
      return (this.disciplineLabels && this.disciplineLabels[value]) || value;
    }

    // Restart a subtle fade-in animation on an element.
    fadeIn(el) {
      if (!el) return;
      el.classList.remove('is-fade-in');
      // Force reflow so the animation re-triggers on re-add.
      void el.offsetWidth;
      el.classList.add('is-fade-in');
    }

    // Drive the persistent header. `help` blank → intro hidden (no gap).
    setHeader(title, help) {
      if (this.titleEl) this.titleEl.textContent = title || '';
      if (this.introEl) {
        const h = (help || '').trim();
        this.introEl.textContent = h;
        if (h) this.introEl.removeAttribute('hidden');
        else this.introEl.setAttribute('hidden', '');
      }
    }

    // 'landing' | 'wizard' | 'results'. Drives dark theme / background image
    // scoping — those apply to every phase except results.
    setPhase(phase) {
      this.dataset.phase = phase;
    }

    // Reflect the active results view (Product = classic / Discipline =
    // experimental) on the on-page toggle.
    updateViewToggle() {
      if (!this.viewToggleEl) return;
      this.viewToggleEl.querySelectorAll('[data-view]').forEach((b) => {
        const on = b.getAttribute('data-view') === this.resultsLayout;
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      // Slide the thumb to the active option (after layout settles).
      requestAnimationFrame(() => this.positionViewThumb());
    }

    // Bring the finder back into view on a transition so the user isn't
    // stranded scrolled-down. Honours reduced-motion.
    scrollToTop() {
      if (this.suppressScroll) return;
      const reduce = window.matchMedia &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      try {
        this.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
      } catch (e) {
        try { this.scrollIntoView(true); } catch (e2) { /* noop */ }
      }
    }

    // Overlay a sliding thumb exactly on the active button so the pill
    // hugs each word with equal padding (words differ in length).
    moveThumb(containerEl, activeEl) {
      if (!containerEl || !activeEl) return;
      const cRect = containerEl.getBoundingClientRect();
      const aRect = activeEl.getBoundingClientRect();
      if (!aRect.width) return; // not visible / not laid out yet
      const x = aRect.left - cRect.left - containerEl.clientLeft;
      containerEl.style.setProperty('--thumb-w', aRect.width + 'px');
      containerEl.style.setProperty('--thumb-x', x + 'px');
    }

    positionToggleThumb() {
      if (!this.toggleEl) return;
      this.moveThumb(
        this.toggleEl,
        this.toggleEl.querySelector('.foil-finder__toggle-btn.is-active') ||
          this.toggleEl.querySelector('.foil-finder__toggle-btn')
      );
    }

    positionViewThumb() {
      if (!this.viewSegEl) return;
      this.moveThumb(
        this.viewSegEl,
        this.viewSegEl.querySelector('.foil-finder__seg-btn.is-active') ||
          this.viewSegEl.querySelector('.foil-finder__seg-btn')
      );
    }

    // --- State persistence (survives navigating to a product and back) ---

    saveState() {
      // Only persist once the journey has started; the landing screen and
      // pre-Go toggle choices are intentionally not remembered.
      if (!this.started) return;
      try {
        const payload = {
          startMode: this.startMode,
          started: true,
          completed: !!this.completed,
          stepIndex: this.stepIndex,
          disciplines: Array.from(this.state.disciplines),
          abilities: Array.from(this.state.abilities),
          weight: this.state.weight,
          resultsLayout: this.resultsLayout,
        };
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
      } catch (e) { /* storage unavailable (private mode etc.) */ }
    }

    clearState() {
      try { sessionStorage.removeItem(STORAGE_KEY); } catch (e) { /* noop */ }
    }

    readState() {
      try {
        const raw = sessionStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const data = JSON.parse(raw);
        return data && data.started ? data : null;
      } catch (e) {
        return null;
      }
    }

    // Mirror the in-memory selections onto the chip DOM (used on restore).
    reflectSelections() {
      const mark = (containerEl, set) => {
        if (!containerEl) return;
        containerEl.querySelectorAll('.foil-finder__chip').forEach((chip) => {
          const on = set.has(chip.dataset.value);
          chip.classList.toggle('is-selected', on);
          chip.setAttribute('aria-checked', on ? 'true' : 'false');
        });
      };
      mark(this.abilitiesEl, this.state.abilities);
      mark(this.disciplinesEl, this.state.disciplines);
      if (this.weightsEl) {
        this.weightsEl.querySelectorAll('.foil-finder__chip').forEach((chip) => {
          const on = this.state.weight !== null && chip.dataset.value === this.state.weight;
          chip.classList.toggle('is-selected', on);
          chip.setAttribute('aria-checked', on ? 'true' : 'false');
        });
      }
      this.applyAbilityConstraints();
    }

    // Rebuild the UI from a saved state on page load.
    restore(saved) {
      this.suppressScroll = true;

      // Restore the start mode (order + toggle UI).
      const mode = this.orderings[saved.startMode] ? saved.startMode : 'discipline';
      this.startMode = mode;
      this.stepOrder = this.orderings[mode].map((k) => this.stepConfigs[k]);
      this.querySelectorAll('[data-start-mode]').forEach((b) => {
        const on = b.getAttribute('data-start-mode') === mode;
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      if (this.toggleEl) this.toggleEl.dataset.mode = mode;

      // Restore the chosen results view (Product / Discipline).
      if (saved.resultsLayout === 'classic' || saved.resultsLayout === 'experimental') {
        this.resultsLayout = saved.resultsLayout;
      }

      // Restore selections.
      this.state.disciplines = new Set(saved.disciplines || []);
      this.state.abilities = new Set(saved.abilities || []);
      this.state.weight = saved.weight != null ? saved.weight : null;
      this.started = true;
      this.reflectSelections();

      if (saved.completed) {
        this.submit();
      } else {
        this.startWizard();
        const idx = typeof saved.stepIndex === 'number' ? saved.stepIndex : 0;
        this.showStep(idx, false);
      }

      this.suppressScroll = false;
      this.saveState();
    }

    bindEvents() {
      this.addEventListener('click', (e) => {
        const chip = e.target.closest('.foil-finder__chip');
        if (chip) {
          if (chip.disabled || chip.classList.contains('is-disabled')) return;
          this.toggleChip(chip);
          this.applyAbilityConstraints();
          this.updateNav();
          // Keep the definition highlight in sync while on the ability step.
          if (this.currentStepKey() === 'ability') this.renderDefinitions();
          this.updateDefHint();
          this.saveState();
          return;
        }
        if (e.target.closest('[data-finder-go]')) {
          this.startWizard();
          return;
        }
        if (e.target.closest('[data-finder-next]')) {
          this.goNext();
          return;
        }
        if (e.target.closest('[data-finder-back]')) {
          this.goBack();
          return;
        }
        const startBtn = e.target.closest('[data-start-mode]');
        if (startBtn) {
          this.setStartMode(startBtn.getAttribute('data-start-mode'));
          return;
        }
        if (e.target.closest('[data-finder-edit]')) {
          this.editAnswers();
          return;
        }
        if (e.target.closest('[data-finder-reset]')) {
          this.reset();
          return;
        }
        const moreBtn = e.target.closest('[data-ff-more]');
        if (moreBtn) {
          const card = moreBtn.closest('.foil-finder__card');
          const panel = card && card.querySelector('[data-ff-morepanel]');
          const open = moreBtn.getAttribute('aria-expanded') === 'true';
          moreBtn.setAttribute('aria-expanded', String(!open));
          moreBtn.textContent = open ? 'More info' : 'Hide info';
          if (panel) panel.classList.toggle('is-open', !open);
          return;
        }
        const viewBtn = e.target.closest('[data-view]');
        if (viewBtn) {
          const v = viewBtn.getAttribute('data-view');
          if (v && v !== this.resultsLayout) {
            this.resultsLayout = v;
            this.updateViewToggle();
            this.applyFilters(); // re-render results in the chosen layout
            this.saveState();
          }
        }
      });
    }

    currentStepKey() {
      return this.stepOrder[this.stepIndex].key;
    }

    currentStepValid() {
      switch (this.currentStepKey()) {
        case 'ability': return this.state.abilities.size > 0;
        case 'weight': return this.state.weight !== null;
        case 'discipline': return this.state.disciplines.size > 0;
        default: return false;
      }
    }

    updateNav() {
      if (this.nextBtn) this.nextBtn.disabled = !this.currentStepValid();
    }

    showStep(index, focus = true) {
      this.setPhase('wizard');
      this.stepIndex = Math.max(0, Math.min(index, this.stepOrder.length - 1));
      const activeKey = this.currentStepKey();

      Object.keys(this.stepEls).forEach((key) => {
        const el = this.stepEls[key];
        if (key === activeKey) el.removeAttribute('hidden');
        else el.setAttribute('hidden', '');
      });

      // The step's question/help become the persistent header.
      const stepEl = this.stepEls[activeKey];
      if (stepEl) this.setHeader(stepEl.dataset.stepTitle, stepEl.dataset.stepHelp);

      const isLast = this.stepIndex === this.stepOrder.length - 1;
      // Back is always available — from the first step it returns to the
      // landing (Start with) screen.
      if (this.backBtn) this.backBtn.removeAttribute('hidden');
      if (this.nextBtn) {
        this.nextBtn.textContent = isLast ? 'See results' : 'Next';
      }

      this.renderProgress();
      this.updateNav();
      this.fadeIn(this.stepsEl);
      // Definitions panel now lives below the nav (outside the step), so it
      // must be explicitly cleared when leaving the ability step.
      if (activeKey === 'ability') this.renderDefinitions();
      else if (this.definitionsEl) this.definitionsEl.innerHTML = '';
      this.updateDefHint();
      this.scrollToTop();

      // Move focus to the first chip of the step for keyboard users
      // (skipped on initial page load so we don't steal focus / jump).
      if (focus) {
        const firstChip = this.stepEls[activeKey].querySelector('.foil-finder__chip');
        if (firstChip) firstChip.focus({ preventScroll: true });
      }
    }

    // Show the "Read below…" hint only on the ability step once a level
    // has been picked (the definitions render below the nav).
    updateDefHint() {
      if (!this.defHintEl) return;
      const show = this.currentStepKey() === 'ability' && this.state.abilities.size > 0;
      if (show) this.defHintEl.removeAttribute('hidden');
      else this.defHintEl.setAttribute('hidden', '');
    }

    renderDefinitions() {
      const panel = this.definitionsEl;
      if (!panel) return;
      if (!this.definitions || !Object.keys(this.definitions).length) {
        panel.innerHTML = '';
        return;
      }

      const allDisc = (this.data && this.data.filters && this.data.filters.disciplines) || [];
      const allAbil = (this.data && this.data.filters && this.data.filters.abilities) || [];
      const selectedDisc = this.state.disciplines;
      const selectedAbil = this.state.abilities;

      // Nothing shows until the rider clicks an ability level.
      if (selectedAbil.size === 0) {
        panel.innerHTML = '';
        return;
      }

      // Discipline-first flow → only the chosen disciplines.
      // Ability-first flow (none chosen yet) → every discipline.
      const show = allDisc.filter((d) => {
        if (!this.definitions[d]) return false;
        return selectedDisc.size === 0 || selectedDisc.has(d);
      });

      if (!show.length) {
        panel.innerHTML = '';
        return;
      }

      // Only the selected ability level(s), in canonical order.
      const levelsToShow = allAbil.filter((a) => selectedAbil.has(a));

      const blocks = show
        .map((disc) => {
          const def = this.definitions[disc];
          const rows = levelsToShow
            .filter((a) => def.levels && def.levels[a])
            .map((a) => (
              '<li class="foil-finder__def-level is-selected">' +
                '<span class="foil-finder__def-level-name">' + this.escape(a) + '</span>' +
                '<span class="foil-finder__def-level-text">' + this.escape(def.levels[a]) + '</span>' +
              '</li>'
            ))
            .join('');
          if (!rows) return '';
          return (
            '<div class="foil-finder__def">' +
              '<h4 class="foil-finder__def-title">' + this.escape(this.discLabel(disc)) + '</h4>' +
              '<ul class="foil-finder__def-levels">' + rows + '</ul>' +
            '</div>'
          );
        })
        .filter(Boolean);

      panel.innerHTML = blocks.join('');
      if (blocks.length) this.fadeIn(panel);
    }

    renderProgress() {
      if (!this.progressEl) return;
      const parts = [];
      this.stepOrder.forEach((s, idx) => {
        let cls = 'foil-finder__progress-step';
        if (this.completed || idx < this.stepIndex) cls += ' is-done';
        if (!this.completed && idx === this.stepIndex) cls += ' is-active';
        const current = !this.completed && idx === this.stepIndex ? ' aria-current="step"' : '';
        parts.push(
          '<li class="' + cls + '" data-index="' + (idx + 1) + '"' + current + '>' +
            '<span class="foil-finder__progress-label">' + this.escape(s.label) + '</span>' +
          '</li>'
        );
        if (idx < this.stepOrder.length - 1) {
          parts.push('<li class="foil-finder__progress-sep" aria-hidden="true">&rsaquo;</li>');
        }
      });
      this.progressEl.innerHTML = parts.join('');
    }

    goNext() {
      if (!this.currentStepValid()) return;
      if (this.stepIndex === this.stepOrder.length - 1) {
        this.submit();
      } else {
        this.showStep(this.stepIndex + 1);
        this.saveState();
      }
    }

    goBack() {
      if (this.completed) {
        this.editAnswers();
        return;
      }
      if (this.stepIndex > 0) {
        this.showStep(this.stepIndex - 1);
        this.saveState();
        return;
      }
      // Back from the first step returns to the landing (Start with) screen.
      this.showLanding(true);
    }

    // Landing screen: progress + header + intro + the Start-with toggle/Go.
    // Only the wizard steps and results stay hidden until "Go".
    showLanding(scroll = false) {
      this.setPhase('landing');
      this.started = false;
      this.completed = false;
      this.stepIndex = 0;
      // Returning to the landing screen (Reset / Back-to-start) is a fresh
      // start — drop any persisted answers.
      this.clearState();
      this.state = { disciplines: new Set(), abilities: new Set(), weight: null };
      this.querySelectorAll('.foil-finder__chip.is-selected').forEach((c) => {
        c.classList.remove('is-selected');
        c.setAttribute('aria-checked', 'false');
      });
      this.applyAbilityConstraints();
      this.resultsEl.innerHTML = '';
      if (this.summaryEl) this.summaryEl.innerHTML = '';
      this.setStatus('');
      if (this.startmodeEl) this.startmodeEl.removeAttribute('hidden');
      if (this.headerEl) this.headerEl.removeAttribute('hidden');
      if (this.progressEl) this.progressEl.removeAttribute('hidden');
      if (this.wizardEl) this.wizardEl.setAttribute('hidden', '');
      if (this.resultsbarEl) this.resultsbarEl.setAttribute('hidden', '');
      if (this.viewToggleEl) this.viewToggleEl.setAttribute('hidden', '');
      if (this.statusbarEl) this.statusbarEl.setAttribute('hidden', '');
      // Restore the default header (e.g. "Find your setup" + intro sentence).
      this.setHeader(this.defaultTitle, this.defaultIntro);
      this.renderProgress();
      // Toggle is now visible — measure once layout has settled.
      requestAnimationFrame(() => this.positionToggleThumb());
      if (scroll) this.scrollToTop();
    }

    // Go pressed: enter the wizard at the first step.
    startWizard() {
      this.started = true;
      this.completed = false;
      if (this.startmodeEl) this.startmodeEl.setAttribute('hidden', '');
      if (this.headerEl) this.headerEl.removeAttribute('hidden');
      if (this.progressEl) this.progressEl.removeAttribute('hidden');
      if (this.wizardEl) this.wizardEl.removeAttribute('hidden');
      if (this.resultsbarEl) this.resultsbarEl.setAttribute('hidden', '');
      if (this.viewToggleEl) this.viewToggleEl.setAttribute('hidden', '');
      if (this.statusbarEl) this.statusbarEl.setAttribute('hidden', '');
      if (this.introEl) this.introEl.removeAttribute('hidden');
      this.resultsEl.innerHTML = '';
      if (this.summaryEl) this.summaryEl.innerHTML = '';
      this.showStep(0);
      this.saveState();
    }

    submit() {
      this.completed = true;
      this.setPhase('results');
      if (this.wizardEl) this.wizardEl.setAttribute('hidden', '');
      if (this.progressEl) this.progressEl.setAttribute('hidden', '');
      if (this.startmodeEl) this.startmodeEl.setAttribute('hidden', '');
      if (this.headerEl) this.headerEl.removeAttribute('hidden');
      // Results header reverts to the default title; the intro is replaced
      // by the results summary sentence (rendered into the summary slot).
      if (this.titleEl) this.titleEl.textContent = this.defaultTitle;
      if (this.introEl) this.introEl.setAttribute('hidden', '');
      if (this.resultsbarEl) this.resultsbarEl.removeAttribute('hidden');
      if (this.viewToggleEl) this.viewToggleEl.removeAttribute('hidden');
      this.updateViewToggle();
      if (this.statusbarEl) this.statusbarEl.removeAttribute('hidden');
      this.applyFilters();
      this.scrollToTop();
      this.saveState();
    }

    editAnswers() {
      this.completed = false;
      this.started = true;
      // Editing answers clears the saved state (per requirement).
      this.clearState();
      if (this.wizardEl) this.wizardEl.removeAttribute('hidden');
      if (this.headerEl) this.headerEl.removeAttribute('hidden');
      if (this.progressEl) this.progressEl.removeAttribute('hidden');
      // Stay in the wizard (not the landing screen).
      if (this.startmodeEl) this.startmodeEl.setAttribute('hidden', '');
      if (this.introEl) this.introEl.removeAttribute('hidden');
      if (this.resultsbarEl) this.resultsbarEl.setAttribute('hidden', '');
      if (this.viewToggleEl) this.viewToggleEl.setAttribute('hidden', '');
      if (this.statusbarEl) this.statusbarEl.setAttribute('hidden', '');
      this.resultsEl.innerHTML = '';
      if (this.summaryEl) this.summaryEl.innerHTML = '';
      this.setStatus('');
      // Return to the last step so they can tweak; selections are preserved.
      this.showStep(this.stepOrder.length - 1);
    }

    toggleChip(chip) {
      const group = chip.dataset.group;
      const value = chip.dataset.value;
      if (group === 'weight') {
        const same = this.state.weight === value;
        this.state.weight = same ? null : value;
        this.weightsEl.querySelectorAll('.foil-finder__chip').forEach((c) => {
          const on = !same && c.dataset.value === value;
          c.classList.toggle('is-selected', on);
          c.setAttribute('aria-checked', on ? 'true' : 'false');
        });
        return;
      }
      // Irregular plural: data-group is "ability" but the state key is "abilities".
      const key = group === 'ability' ? 'abilities' : group + 's';
      const set = this.state[key];
      if (!set) return;
      if (set.has(value)) {
        set.delete(value);
        chip.classList.remove('is-selected');
        chip.setAttribute('aria-checked', 'false');
      } else {
        set.add(value);
        chip.classList.add('is-selected');
        chip.setAttribute('aria-checked', 'true');
      }
    }

    setStartMode(mode) {
      if (!this.orderings[mode] || mode === this.startMode) return;
      this.startMode = mode;
      this.stepOrder = this.orderings[mode].map((k) => this.stepConfigs[k]);
      this.querySelectorAll('[data-start-mode]').forEach((b) => {
        const on = b.getAttribute('data-start-mode') === mode;
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      if (this.toggleEl) this.toggleEl.dataset.mode = mode;
      // Slide the thumb to the newly-active button.
      this.positionToggleThumb();
      // Reflect the new order in the landing progress indicator.
      this.renderProgress();
      // The journey begins when "Go" is pressed.
    }

    // Reset returns to the landing (Start with) screen.
    reset() {
      this.showLanding(true);
    }

    matches() {
      const { disciplines, abilities, weight } = this.state;
      if (disciplines.size === 0 && abilities.size === 0 && !weight) return [];
      return this.data.recommendations.filter((r) => {
        // "*" wildcard (from CSV) matches any selection for that field.
        if (disciplines.size && r.discipline !== '*' && !disciplines.has(r.discipline)) return false;
        if (abilities.size && r.ability !== '*' && !abilities.has(r.ability)) return false;
        // Rows with no weight_label (JSON masts/stabilizers/fuselages) or "*"
        // (CSV) match any weight.
        if (weight && r.weight_label && r.weight_label !== '*' && r.weight_label !== weight) return false;
        return true;
      });
    }

    buildSummary(count) {
      const allAbil = (this.data && this.data.filters && this.data.filters.abilities) || [];
      const allDisc = (this.data && this.data.filters && this.data.filters.disciplines) || [];

      // Canonical order, only the selected values (mapped to display names).
      const abil = allAbil.filter((a) => this.state.abilities.has(a));
      const disc = allDisc
        .filter((d) => this.state.disciplines.has(d))
        .map((d) => this.discLabel(d));

      const abilityText = abil.join(' - ');

      let disciplineText = '';
      if (disc.length === 1) {
        disciplineText = disc[0];
      } else if (disc.length === 2) {
        disciplineText = disc[0] + ' and ' + disc[1];
      } else if (disc.length > 2) {
        disciplineText = disc.slice(0, -1).join(', ') + ' and ' + disc[disc.length - 1];
      }

      if (!abilityText || !disciplineText) return '';

      const n = typeof count === 'number' ? count : 0;
      const productWord = n === 1 ? 'product' : 'products';

      return (
        '<p class="foil-finder__summary">' +
          'Here are ' + n + ' matching ' + productWord + ' for a ' +
          '<strong>' + this.escape(abilityText) + '</strong> rider focused on ' +
          '<strong>' + this.escape(disciplineText) + '</strong> <strong>foiling</strong>, ' +
          'tailored to your weight range.' +
        '</p>'
      );
    }

    async applyFilters() {
      const { disciplines, abilities, weight } = this.state;
      if (disciplines.size === 0 || abilities.size === 0 || !weight) {
        this.resultsEl.innerHTML = '';
        if (this.summaryEl) this.summaryEl.innerHTML = '';
        const missing = [];
        if (disciplines.size === 0) missing.push('discipline');
        if (abilities.size === 0) missing.push('ability');
        if (!weight) missing.push('weight');
        this.setStatus('Select your ' + missing.join(', ') + ' to see matches.');
        return;
      }

      const rows = this.matches();
      if (rows.length === 0) {
        this.resultsEl.innerHTML = '';
        if (this.summaryEl) this.summaryEl.innerHTML = '';
        this.setStatus('No matches for this combination. Try widening your selection.');
        return;
      }

      // One card per product (handle) within a category; the recommended
      // sizes are collected onto that single product.
      const byCategory = new Map(); // category -> Map(handle -> product)
      // Display order: keep the data order but show Boards last.
      const order = this.data.filters.categories.slice();
      const boardIdx = order.indexOf('Board');
      if (boardIdx !== -1) {
        order.splice(boardIdx, 1);
        order.push('Board');
      }
      // Selected disciplines in stable (data) order — used by the
      // experimental columns layout. Classic ignores p.disciplines.
      const selDisc = (this.data.filters.disciplines || [])
        .filter((d) => this.state.disciplines.has(d));
      for (const r of rows) {
        if (!byCategory.has(r.category)) byCategory.set(r.category, new Map());
        const prods = byCategory.get(r.category);
        let p = prods.get(r.handle);
        if (!p) {
          p = {
            handle: r.handle, name: r.name || '', family: r.family || '',
            sizes: new Set(), disciplines: new Set(), sizeDisc: {},
          };
          prods.set(r.handle, p);
        }
        const s = (r.size == null ? '' : String(r.size)).trim();
        // Disciplines this row applies to (wildcard = all selected).
        const rowDisc = r.discipline === '*'
          ? selDisc.slice()
          : (this.state.disciplines.has(r.discipline) ? [r.discipline] : []);
        rowDisc.forEach((d) => p.disciplines.add(d));
        if (s) {
          p.sizes.add(s);
          // Per-size → which selected disciplines recommend it (for the
          // experimental discipline-aligned size bars). Classic ignores this.
          if (!p.sizeDisc[s]) p.sizeDisc[s] = new Set();
          rowDisc.forEach((d) => p.sizeDisc[s].add(d));
        }
      }
      let productCount = 0;
      byCategory.forEach((m) => { productCount += m.size; });

      // The count now lives in the summary sentence, so drop the separate
      // status line and hide the (otherwise empty) status bar.
      this.setStatus('');
      if (this.statusbarEl) this.statusbarEl.setAttribute('hidden', '');
      if (this.summaryEl) this.summaryEl.innerHTML = this.buildSummary(productCount);
      const groupsHTML = order
        .filter((c) => byCategory.has(c))
        .map((c) => this.renderCategory(c, Array.from(byCategory.get(c).values())))
        .join('');
      this.resultsEl.innerHTML =
        (this.resultsLayout === 'experimental'
          ? this.expColumnsHeaderHTML(selDisc)
          : '') + groupsHTML;
      if (this.resultsLayout === 'experimental') this.bindExpStickySync();
      this.fadeIn(this.summaryEl);
      this.fadeIn(this.resultsEl);

      const handles = Array.from(new Set(rows.map((r) => r.handle)));
      await Promise.all(handles.map((h) => this.hydrate(h)));
    }

    // Sort size strings smallest → biggest by their leading number
    // ("725mm" < "795mm", "90L" etc.). Non-numeric sizes sort last.
    sortSizes(list) {
      const num = (s) => {
        const m = String(s).match(/[\d.]+/);
        return m ? parseFloat(m[0]) : Number.POSITIVE_INFINITY;
      };
      return list.slice().sort((a, b) => {
        const na = num(a);
        const nb = num(b);
        if (na !== nb) return na - nb;
        return String(a).localeCompare(String(b));
      });
    }

    // Dispatcher: Classic (current) vs Experimental layout. Driven by the
    // "Results layout" section setting. Removing the experiment later =
    // delete categoryHTMLExperimental + this branch + the schema setting.
    renderCategory(category, products) {
      if (this.resultsLayout === 'experimental') {
        return this.categoryHTMLExperimental(category, products);
      }
      return this.categoryHTML(category, products);
    }

    // A product card with no image (used by the experimental layout).
    // Keeps the data-handle / data-card-title / data-oneliner hooks so the
    // existing hydrate() fills title + one-liner (and skips the image).
    // Card for the experimental layout. The card spans outer columns a..b
    // (0-based selDisc indices). Inside, each recommended size is a darker
    // "bar" placed under the discipline column(s) that size is good for —
    // so sizes span their disciplines just like the card does.
    expCardHTML(p, a, b, selDisc) {
      const href = '/products/' + encodeURIComponent(p.handle);
      const placeholder = (p.family || p.name || '').trim();
      const span = b - a + 1;
      const sizes = this.sortSizes(Array.from(p.sizes));
      const sizeDisc = p.sizeDisc || {};

      let barsHTML = '';
      sizes.forEach((sz) => {
        const ds = sizeDisc[sz];
        if (!ds) return;
        // Local sub-column indices (0..span-1) where this size applies.
        const local = [];
        for (let i = a; i <= b; i += 1) {
          if (ds.has(selDisc[i])) local.push(i - a);
        }
        if (!local.length) return;
        this.contiguousRuns(local).forEach(([la, lb]) => {
          barsHTML +=
            '<div class="ff-exp__size-bar" style="grid-column:' +
              (la + 1) + ' / span ' + (lb - la + 1) + '">' +
              this.escape(sz) +
            '</div>';
        });
      });

      const sizesBlock = barsHTML
        ? '<div class="ff-exp__sizes" style="--ff-sub:' + span + '">' + barsHTML + '</div>'
        : '';

      return (
        '<a class="foil-finder__card ff-exp__card" style="grid-column:' +
          (a + 1) + ' / span ' + span + '"' +
          ' data-handle="' + this.escape(p.handle) + '" href="' + href + '">' +
          '<div class="foil-finder__card-body">' +
            '<div class="foil-finder__card-title" data-card-title>' + this.escape(placeholder) + '</div>' +
            '<div class="foil-finder__card-oneliner" data-oneliner></div>' +
            sizesBlock +
          '</div>' +
        '</a>'
      );
    }

    // Split a sorted list of column indices into contiguous runs.
    // [0,1,3] -> [[0,1],[3]]
    contiguousRuns(idxs) {
      if (!idxs.length) return [];
      const sorted = idxs.slice().sort((a, b) => a - b);
      const runs = [];
      let start = sorted[0];
      let prev = sorted[0];
      for (let k = 1; k < sorted.length; k += 1) {
        if (sorted[k] === prev + 1) {
          prev = sorted[k];
        } else {
          runs.push([start, prev]);
          start = sorted[k];
          prev = sorted[k];
        }
      }
      runs.push([start, prev]);
      return runs;
    }

    // Single discipline-title bar shown once at the top of the experimental
    // results and made sticky on scroll (desktop and mobile — see CSS).
    expColumnsHeaderHTML(selDisc) {
      if (!selDisc || !selDisc.length) return '';
      const cells = selDisc
        .map((d, i) =>
          '<div class="ff-exp__col-title' + (i > 0 ? ' ff-exp__bordered' : '') + '">' +
            this.escape(this.discLabel(d)) +
          '</div>')
        .join('');
      return (
        '<div class="ff-exp__topbar" style="--ff-cols:' + selDisc.length + '">' +
          '<div class="ff-exp__topbar-grid">' + cells + '</div>' +
        '</div>'
      );
    }

    // EXPERIMENTAL results layout: discipline columns. A product spans the
    // contiguous run(s) of columns it's recommended for — so a 2-of-3 match
    // is ONE card spanning those two columns (not duplicated). Matching all
    // selected disciplines spans the full width. Classic is untouched.
    categoryHTMLExperimental(category, products) {
      const selDisc = ((this.data && this.data.filters && this.data.filters.disciplines) || [])
        .filter((d) => this.state.disciplines.has(d));
      const nCols = Math.max(selDisc.length, 1);

      let cardsHTML = '';
      products.forEach((p) => {
        const pd = p.disciplines || new Set();
        let idxs = selDisc
          .map((d, i) => (pd.has(d) ? i : -1))
          .filter((i) => i >= 0);
        // No recorded discipline (e.g. wildcard with none selected) → full width.
        if (idxs.length === 0) idxs = selDisc.map((_, i) => i);
        const runs = this.contiguousRuns(idxs);
        runs.forEach(([a, b]) => {
          cardsHTML += this.expCardHTML(p, a, b, selDisc);
        });
      });

      const categoryTitle = /s$/i.test(category) ? category : category + 's';
      return (
        '<div class="foil-finder__group ff-exp" style="--ff-cols:' + nCols + '">' +
          '<h3 class="foil-finder__group-title">' + this.escape(categoryTitle) + '</h3>' +
          '<div class="ff-exp__scroll" data-exp-scroll>' +
            '<div class="ff-exp__grid">' + cardsHTML + '</div>' +
          '</div>' +
        '</div>'
      );
    }

    // Mobile: there's now ONE sticky discipline bar for all categories, but
    // each category is its own horizontal swipe area. Keep every category's
    // scroll position in unison and translate the single top bar's inner
    // grid to match, so the bar stays aligned with whichever category the
    // user is swiping. The `lastLeft` guard ignores the mirrored (non-user)
    // scroll events we trigger when syncing the other scrollers, preventing
    // a feedback loop without fighting iOS momentum scrolling.
    bindExpStickySync() {
      const scrollers = Array.from(this.querySelectorAll('[data-exp-scroll]'));
      if (!scrollers.length) return;
      const topbarGrid = this.querySelector('.ff-exp__topbar-grid');
      let lastLeft = -1;
      let queued = false;

      scrollers.forEach((scroller) => {
        scroller.addEventListener('scroll', () => {
          if (scroller.scrollLeft === lastLeft) return; // mirrored, not user-driven
          if (queued) return;
          queued = true;
          requestAnimationFrame(() => {
            const left = scroller.scrollLeft;
            lastLeft = left;
            scrollers.forEach((s) => {
              if (s !== scroller && s.scrollLeft !== left) s.scrollLeft = left;
            });
            if (topbarGrid) {
              topbarGrid.style.transform = 'translateX(' + (-left) + 'px)';
            }
            queued = false;
          });
        }, { passive: true });
      });
    }

    // Join already-escaped HTML fragments into a natural-language list:
    // ["a"] -> "a", ["a","b"] -> "a and b", ["a","b","c"] -> "a, b and c".
    joinList(items) {
      if (items.length <= 1) return items.join('');
      return items.slice(0, -1).join(', ') + ' and ' + items[items.length - 1];
    }

    // Join full clauses into one flowing sentence (clause-level "and"):
    // "c1" -> "c1", "c1","c2" -> "c1, and c2",
    // "c1","c2","c3" -> "c1, c2, and c3".
    joinClauses(clauses) {
      if (clauses.length <= 1) return clauses.join('');
      return clauses.slice(0, -1).join(', ') + ', and ' + clauses[clauses.length - 1];
    }

    // Invert a product's per-size discipline map into a per-discipline list
    // of recommended sizes, in stable selected-discipline order. Returns
    // [{ label, sizes:[...] }] only for disciplines that have sizes.
    sizesByDiscipline(p, selDisc) {
      const sizeDisc = p.sizeDisc || {};
      const sizeKeys = Object.keys(sizeDisc);
      return selDisc
        .map((d) => {
          const list = sizeKeys.filter((sz) => sizeDisc[sz] && sizeDisc[sz].has(d));
          return { label: this.discLabel(d), sizes: this.sortSizes(list) };
        })
        .filter((x) => x.sizes.length);
    }

    categoryHTML(category, products) {
      // Selected disciplines in stable (data) order — drives the optional
      // per-discipline size breakdown on each card.
      const selDisc = ((this.data && this.data.filters && this.data.filters.disciplines) || [])
        .filter((d) => this.state.disciplines.has(d));
      const cards = products
        .map((p) => {
          // Placeholder until the Shopify product title loads (also the
          // graceful fallback if the product fetch fails).
          const placeholder = (p.family || p.name || '').trim();
          const sizes = this.sortSizes(Array.from(p.sizes));
          // Per-discipline size recommendations, surfaced as one flowing
          // sentence (see grouping below).
          const breakdown = this.sizesByDiscipline(p, selDisc);
          // Group disciplines that share the exact same recommended sizes,
          // preserving first-seen order. A product good for several (or all)
          // selected disciplines then reads as one clause — e.g. "For
          // Winging and Parawing we recommend 850 and 1050." Disciplines
          // with different sizes stay as separate clauses.
          const groups = [];
          breakdown.forEach((b) => {
            const sig = b.sizes.join('|');
            const g = groups.find((x) => x.sig === sig);
            if (g) g.labels.push(b.label);
            else groups.push({ sig: sig, labels: [b.label], sizes: b.sizes });
          });
          // Shown when more than one discipline is selected and at least one
          // has recommended sizes (a single discipline would just repeat the
          // "Recommended sizes" line above).
          const showBreakdown = selDisc.length > 1 && groups.length > 0;
          const breakdownHTML = showBreakdown
            ? '<div class="foil-finder__card-sizes-bd">' +
                '<p class="foil-finder__card-sizes-sentence">' +
                  this.joinClauses(
                    groups.map((g, i) =>
                      (i === 0 ? 'For ' : 'for ') +
                      this.joinList(
                        g.labels.map((l) => '<strong>' + this.escape(l) + '</strong>')
                      ) +
                      ' we recommend ' +
                      this.joinList(
                        g.sizes.map((s) => '<strong>' + this.escape(s) + '</strong>')
                      )
                    )
                  ) + '.' +
                '</p>' +
              '</div>'
            : '';
          const sizesHTML = sizes.length
            ? '<div class="foil-finder__card-sizes" data-sizes>' +
                '<span class="foil-finder__card-sizes-label">Recommended sizes</span>' +
                '<span class="foil-finder__card-sizes-value">' + this.escape(sizes.join(', ')) + '</span>' +
                breakdownHTML +
              '</div>'
            : '';
          // Size recommendations are now hidden behind a "More info" toggle
          // that expands/collapses with an animation. No whole-card link.
          const moreHTML = sizesHTML
            ? '<button type="button" class="foil-finder__card-more-btn"' +
                ' data-ff-more aria-expanded="false">More info</button>' +
              '<div class="foil-finder__card-more" data-ff-morepanel>' +
                '<div class="foil-finder__card-more-inner">' + sizesHTML + '</div>' +
              '</div>'
            : '';
          return (
            '<div class="foil-finder__card" data-handle="' + this.escape(p.handle) + '">' +
              '<div class="foil-finder__card-media" data-media></div>' +
              '<div class="foil-finder__card-body">' +
                '<div class="foil-finder__card-title" data-card-title>' + this.escape(placeholder) + '</div>' +
                '<div class="foil-finder__card-oneliner" data-oneliner></div>' +
                moreHTML +
              '</div>' +
            '</div>'
          );
        })
        .join('');
      // Pluralise the heading for display only (underlying category value
      // is untouched). Skip if it already ends in "s".
      const categoryTitle = /s$/i.test(category) ? category : category + 's';
      return (
        '<div class="foil-finder__group">' +
          '<h3 class="foil-finder__group-title">' + this.escape(categoryTitle) + '</h3>' +
          '<div class="foil-finder__grid">' + cards + '</div>' +
        '</div>'
      );
    }

    async hydrate(handle) {
      const [product, oneLiner] = await Promise.all([
        loadProduct(handle),
        loadOneLiner(handle),
      ]);
      const cards = this.resultsEl.querySelectorAll('[data-handle="' + CSS.escape(handle) + '"]');
      cards.forEach((card) => {
        const media = card.querySelector('[data-media]');
        const oneLinerEl = card.querySelector('[data-oneliner]');
        const titleEl = card.querySelector('[data-card-title]');

        if (oneLinerEl) oneLinerEl.textContent = oneLiner || '';

        if (!product) return;
        // Title comes from the live Shopify product title.
        if (titleEl && product.title) titleEl.textContent = product.title;
        if (media) {
          // Main product image only (no variant images).
          const src = product.featured_image;
          if (src) {
            const img = new Image();
            img.loading = 'lazy';
            img.alt = product.title || '';
            img.src = src;
            media.innerHTML = '';
            media.appendChild(img);
          }
        }
      });
    }

    setStatus(msg) {
      if (this.statusEl) this.statusEl.textContent = msg;
    }
  }

  if (!customElements.get('foil-finder')) {
    customElements.define('foil-finder', FoilFinder);
  }
})();
